import { defineComponent, ref, computed, watch, createElementBlock, createBlock, openBlock, Fragment, createElementVNode, createVNode, createCommentVNode, withCtx, normalizeClass, toDisplayString, unref, createTextVNode, createSlots, withModifiers, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { RecycleScroller } from "vue-virtual-scroller";
import { Icon } from "@iconify/vue";
import UButton from "@nuxt/ui/components/Button.vue";
import UInput from "@nuxt/ui/components/Input.vue";
import UInputMenu from "@nuxt/ui/components/InputMenu.vue";
import UCheckbox from "@nuxt/ui/components/Checkbox.vue";
import CodSheet from "./index28.js";
import { useIsMobile } from "./index110.js";
const _hoisted_1 = { class: "flex w-full items-center gap-1" };
const _hoisted_2 = { class: "flex h-full min-h-0 flex-col" };
const _hoisted_3 = { class: "shrink-0 p-3" };
const _hoisted_4 = { class: "min-h-0 flex-1 overflow-y-auto" };
const _hoisted_5 = ["onClick"];
const _hoisted_6 = { class: "truncate" };
const _hoisted_7 = {
  key: 2,
  class: "px-4 py-6 text-center text-muted"
};
const _hoisted_8 = {
  key: 0,
  class: "flex min-h-17 shrink-0 items-center gap-2 px-4 py-3"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "CodTypeahead",
  props: {
    modelValue: {
      type: [String, Number, Object, Array],
      required: false
    },
    items: {
      type: Array,
      default: () => []
    },
    itemKey: {
      type: String,
      default: "id"
    },
    itemText: {
      type: [String, Function],
      default: "text"
    },
    itemValue: {
      type: String,
      default: "value"
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** Custom match predicate; default matches on the item text. */
    filter: {
      type: Function,
      default: void 0
    },
    /** Render no options until a query is typed. */
    showFilteredOnly: {
      type: Boolean,
      default: false
    },
    /** Offer a create row when nothing matches; predicate gates on the query. */
    canCreate: {
      type: [Boolean, Function],
      default: false
    },
    /** Label of the create row (receives the query). */
    createLabel: {
      type: Function,
      default: void 0
    },
    /** Trailing "+" affordance while nothing is selected. */
    canAdd: {
      type: Boolean,
      default: false
    },
    /** Full-screen CodSheet picker on mobile; false keeps the dropdown. */
    sheet: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue", "create", "add"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const { t } = useI18n();
    const mobile = useIsMobile();
    const query = ref("");
    const menuOpen = ref(false);
    function getItemText(item) {
      if (!item) return "";
      return typeof props.itemText === "function" ? props.itemText(item) : String(item[props.itemText] ?? "");
    }
    function keyOf(item) {
      return item[props.itemKey] ?? getItemText(item);
    }
    const entries = computed(() => props.items.map((item) => ({
      key: keyOf(item),
      label: getItemText(item),
      item
    })));
    const selectedEntries = computed(() => entries.value.filter((e) => selectedKeys.value.includes(e.key)));
    const filteredEntries = computed(() => {
      const q = query.value.trim();
      if (!q) {
        return props.showFilteredOnly ? selectedEntries.value : entries.value;
      }
      const match = props.filter ? (e) => props.filter(e.item, query.value) : (e) => e.label.toLowerCase().includes(q.toLowerCase());
      const matched = entries.value.filter(match);
      return [...selectedEntries.value, ...matched.filter((e) => !selectedKeys.value.includes(e.key))];
    });
    function modelToKeys(value) {
      const values = Array.isArray(value) ? value : value === null || value === void 0 || value === "" ? [] : [value];
      return values.map(
        (v) => {
          var _a;
          return typeof v === "object" ? keyOf(v) : ((_a = entries.value.find((e) => e.item[props.itemValue] === v)) == null ? void 0 : _a.key) ?? v;
        }
      );
    }
    function keysToModel(keys) {
      const picked = keys.map((k) => {
        var _a;
        return (_a = entries.value.find((e) => e.key === k)) == null ? void 0 : _a.item;
      }).filter((i) => !!i);
      const mapped = props.returnObject ? picked : picked.map((i) => i[props.itemValue]);
      return props.multiple ? mapped : mapped[0];
    }
    const selectedKeys = computed(() => modelToKeys(props.modelValue));
    const hasSelection = computed(() => selectedKeys.value.length > 0);
    const isSelectedKey = (key) => selectedKeys.value.includes(key);
    const menuValue = computed({
      get: () => props.multiple ? selectedKeys.value : selectedKeys.value[0],
      set: (v) => {
        const keys = Array.isArray(v) ? v : v === void 0 || v === null ? [] : [v];
        emit("update:modelValue", keysToModel(keys));
      }
    });
    function clear() {
      emit("update:modelValue", props.multiple ? [] : void 0);
    }
    const createEnabled = computed(() => {
      if (!props.canCreate) return false;
      const q = query.value.trim();
      if (!q) return false;
      return typeof props.canCreate === "function" ? props.canCreate(q) : true;
    });
    const createRowLabel = computed(() => props.createLabel ? props.createLabel(query.value.trim()) : `${t("general.add")}: ${query.value.trim()}`);
    function onCreate() {
      const q = query.value.trim();
      emit("create", q);
      query.value = "";
      menuOpen.value = false;
      if (sheetOpen.value) sheetOpen.value = false;
    }
    const sheetActive = computed(() => props.sheet && mobile.value);
    const sheetOpen = ref(false);
    const sheetSearchRef = ref(null);
    const workingKeys = ref([]);
    const triggerLabel = computed(() => selectedKeys.value.map((k) => {
      var _a;
      return (_a = entries.value.find((e) => e.key === k)) == null ? void 0 : _a.label;
    }).filter(Boolean).join(", "));
    function openSheet() {
      query.value = "";
      workingKeys.value = [...selectedKeys.value];
      sheetOpen.value = true;
      nextTick(() => {
        var _a, _b;
        return (_b = (_a = sheetSearchRef.value) == null ? void 0 : _a.inputRef) == null ? void 0 : _b.focus();
      });
    }
    const isWorkingSelected = (entry) => workingKeys.value.includes(entry.key);
    function onSheetPick(entry) {
      if (!props.multiple) {
        if (isSelectedKey(entry.key)) clear();
        else emit("update:modelValue", keysToModel([entry.key]));
        sheetOpen.value = false;
        return;
      }
      workingKeys.value = isWorkingSelected(entry) ? workingKeys.value.filter((k) => k !== entry.key) : [...workingKeys.value, entry.key];
    }
    function closeSheet(confirmed) {
      if (confirmed) emit("update:modelValue", keysToModel(workingKeys.value));
      sheetOpen.value = false;
    }
    watch(() => props.modelValue, () => {
      query.value = "";
    });
    __expose({ query });
    return (_ctx, _cache) => {
      return sheetActive.value ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
        createElementVNode("div", _hoisted_1, [
          createVNode(UButton, {
            color: "neutral",
            variant: "outline",
            "trailing-icon": "i-lucide-chevron-down",
            class: "min-w-0 flex-1 justify-between font-normal normal-case tracking-normal",
            disabled: __props.disabled,
            onClick: openSheet
          }, {
            default: withCtx(() => [
              createElementVNode("span", {
                class: normalizeClass(["truncate", { "text-dimmed": !triggerLabel.value }])
              }, toDisplayString(triggerLabel.value || __props.placeholder), 3)
            ]),
            _: 1
          }, 8, ["disabled"]),
          hasSelection.value ? (openBlock(), createBlock(UButton, {
            key: 0,
            icon: "i-lucide-x",
            color: "neutral",
            variant: "ghost",
            size: "sm",
            "aria-label": unref(t)("general.close"),
            disabled: __props.disabled,
            onClick: clear
          }, null, 8, ["aria-label", "disabled"])) : __props.canAdd ? (openBlock(), createBlock(UButton, {
            key: 1,
            icon: "i-lucide-plus",
            color: "neutral",
            variant: "ghost",
            size: "sm",
            "aria-label": unref(t)("general.add"),
            disabled: __props.disabled,
            onClick: _cache[0] || (_cache[0] = ($event) => emit("add"))
          }, null, 8, ["aria-label", "disabled"])) : createCommentVNode("", true)
        ]),
        createVNode(CodSheet, {
          modelValue: sheetOpen.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => sheetOpen.value = $event),
          title: __props.placeholder,
          "show-header": ""
        }, {
          default: withCtx(() => [
            createElementVNode("div", _hoisted_2, [
              createElementVNode("div", _hoisted_3, [
                createVNode(UInput, {
                  ref_key: "sheetSearchRef",
                  ref: sheetSearchRef,
                  modelValue: query.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => query.value = $event),
                  type: "search",
                  icon: "i-lucide-search",
                  placeholder: unref(t)("general.search"),
                  class: "w-full"
                }, null, 8, ["modelValue", "placeholder"])
              ]),
              createElementVNode("div", _hoisted_4, [
                filteredEntries.value.length ? (openBlock(), createBlock(unref(RecycleScroller), {
                  key: 0,
                  class: "h-full",
                  items: filteredEntries.value,
                  "item-size": 48,
                  "key-field": "key"
                }, {
                  default: withCtx(({ item: entry }) => [
                    createElementVNode("button", {
                      type: "button",
                      class: "flex h-12 w-full items-center gap-3 px-4 text-left hover:bg-elevated",
                      onClick: ($event) => onSheetPick(entry)
                    }, [
                      __props.multiple ? (openBlock(), createBlock(UCheckbox, {
                        key: 0,
                        "model-value": isWorkingSelected(entry),
                        tabindex: "-1"
                      }, null, 8, ["model-value"])) : createCommentVNode("", true),
                      createElementVNode("span", _hoisted_6, toDisplayString(entry.label), 1),
                      !__props.multiple && isSelectedKey(entry.key) ? (openBlock(), createBlock(unref(Icon), {
                        key: 1,
                        icon: "lucide:check",
                        class: "ml-auto size-4 shrink-0 text-(--app-text-accent)"
                      })) : createCommentVNode("", true)
                    ], 8, _hoisted_5)
                  ]),
                  _: 1
                }, 8, ["items"])) : createEnabled.value ? (openBlock(), createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "flex min-h-12 w-full items-center px-4 py-3 text-left text-(--app-text-accent) hover:bg-elevated",
                  onClick: onCreate
                }, toDisplayString(createRowLabel.value), 1)) : (openBlock(), createElementBlock("p", _hoisted_7, toDisplayString(unref(t)("general.no_data")), 1))
              ]),
              __props.multiple ? (openBlock(), createElementBlock("div", _hoisted_8, [
                createVNode(UButton, {
                  color: "neutral",
                  variant: "outline",
                  size: "lg",
                  class: "flex-1",
                  onClick: _cache[2] || (_cache[2] = ($event) => closeSheet(false))
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(unref(t)("general.cancel")), 1)
                  ]),
                  _: 1
                }),
                createVNode(UButton, {
                  icon: "i-lucide-check",
                  color: "primary",
                  variant: "solid",
                  size: "lg",
                  class: "flex-1",
                  onClick: _cache[3] || (_cache[3] = ($event) => closeSheet(true))
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(unref(t)("general.confirm")), 1)
                  ]),
                  _: 1
                })
              ])) : createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "title"])
      ], 64)) : (openBlock(), createBlock(UInputMenu, {
        key: 1,
        modelValue: menuValue.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => menuValue.value = $event),
        open: menuOpen.value,
        "onUpdate:open": _cache[7] || (_cache[7] = ($event) => menuOpen.value = $event),
        "search-term": query.value,
        "onUpdate:searchTerm": _cache[8] || (_cache[8] = ($event) => query.value = $event),
        items: filteredEntries.value,
        "value-key": "key",
        "label-key": "label",
        "ignore-filter": "",
        "open-on-click": "",
        multiple: __props.multiple,
        placeholder: __props.placeholder,
        disabled: __props.disabled,
        clear: !__props.multiple,
        "create-item": createEnabled.value ? { when: "empty", position: "bottom" } : false,
        virtualize: filteredEntries.value.length > 50,
        class: "w-full",
        onCreate,
        onClear: clear
      }, createSlots({ _: 2 }, [
        createEnabled.value ? {
          name: "create-item-label",
          fn: withCtx(() => [
            createTextVNode(toDisplayString(createRowLabel.value), 1)
          ]),
          key: "0"
        } : void 0,
        __props.canAdd && !hasSelection.value ? {
          name: "trailing",
          fn: withCtx(() => [
            createVNode(UButton, {
              icon: "i-lucide-plus",
              color: "neutral",
              variant: "link",
              size: "sm",
              "aria-label": unref(t)("general.add"),
              onClick: _cache[5] || (_cache[5] = withModifiers(($event) => emit("add"), ["stop"]))
            }, null, 8, ["aria-label"])
          ]),
          key: "1"
        } : void 0
      ]), 1032, ["modelValue", "open", "search-term", "items", "multiple", "placeholder", "disabled", "clear", "create-item", "virtualize"]));
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index29.js.map
